import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Partner, PartnerFilters, CreatePartnerRequest, UpdatePartnerRequest } from "../../../../../api/partners/types";
import { partnersApi } from "../../../../../api/partners/partnersApi";
import type { RootState } from "../../../../../app/rootReducer";

export interface PartnersState {
  partners: Partner[];
  selectedPartner: Partner | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  filters: PartnerFilters;
}

const initialState: PartnersState = {
  partners: [],
  selectedPartner: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,
  filters: {},
};

// Async Thunk Actions
export const fetchPartnersAction = createAsyncThunk(
  "partners/fetchPartners",
  async (filters: PartnerFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await partnersApi.fetchPartners(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch partners");
    }
  }
);

export const fetchPartnerAction = createAsyncThunk(
  "partners/fetchPartner",
  async (id: string, { rejectWithValue }) => {
    try {
      const partner = await partnersApi.fetchPartner(id);
      return partner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch partner");
    }
  }
);

export const createPartnerAction = createAsyncThunk(
  "partners/createPartner",
  async (partnerData: CreatePartnerRequest, { rejectWithValue }) => {
    try {
      const partner = await partnersApi.createPartner(partnerData);
      return partner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create partner");
    }
  }
);

export const updatePartnerAction = createAsyncThunk(
  "partners/updatePartner",
  async ({ id, data }: { id: string; data: UpdatePartnerRequest }, { rejectWithValue }) => {
    try {
      const partner = await partnersApi.updatePartner(id, data);
      return partner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update partner");
    }
  }
);

export const deletePartnerAction = createAsyncThunk(
  "partners/deletePartner",
  async (id: string, { rejectWithValue }) => {
    try {
      await partnersApi.deletePartner(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete partner");
    }
  }
);

export const activatePartnerAction = createAsyncThunk(
  "partners/activatePartner",
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await partnersApi.activatePartner(id);
      // Fetch updated partner data
      const partner = await partnersApi.fetchPartner(id);
      return partner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to activate partner");
    }
  }
);

export const deactivatePartnerAction = createAsyncThunk(
  "partners/deactivatePartner",
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await partnersApi.deactivatePartner(id);
      // Fetch updated partner data
      const partner = await partnersApi.fetchPartner(id);
      return partner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to deactivate partner");
    }
  }
);

const partnersSlice = createSlice({
  name: "partners",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPartners: (state, action: PayloadAction<{ data: Partner[]; total: number; page: number }>) => {
      state.partners = action.payload.data;
      state.totalCount = action.payload.total;
      state.currentPage = action.payload.page;
    },
    setSelectedPartner: (state, action: PayloadAction<Partner | null>) => {
      state.selectedPartner = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<PartnerFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    addPartner: (state, action: PayloadAction<Partner>) => {
      state.partners.unshift(action.payload);
      state.totalCount += 1;
    },
    updatePartner: (state, action: PayloadAction<Partner>) => {
      const index = state.partners.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.partners[index] = action.payload;
      }
      if (state.selectedPartner && state.selectedPartner.id === action.payload.id) {
        state.selectedPartner = action.payload;
      }
    },
    removePartner: (state, action: PayloadAction<string>) => {
      state.partners = state.partners.filter(p => p.id !== action.payload);
      state.totalCount -= 1;
      if (state.selectedPartner && state.selectedPartner.id === action.payload) {
        state.selectedPartner = null;
      }
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Partners
      .addCase(fetchPartnersAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartnersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = action.payload.data;
        state.totalCount = action.payload.total;
        state.currentPage = action.payload.page;
        state.error = null;
      })
      .addCase(fetchPartnersAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Single Partner
      .addCase(fetchPartnerAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartnerAction.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPartner = action.payload;
        state.error = null;
      })
      .addCase(fetchPartnerAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Partner
      .addCase(createPartnerAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPartnerAction.fulfilled, (state, action) => {
        state.loading = false;
        state.partners.unshift(action.payload);
        state.totalCount += 1;
        state.error = null;
      })
      .addCase(createPartnerAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Partner
      .addCase(updatePartnerAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartnerAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.partners.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.partners[index] = action.payload;
        }
        if (state.selectedPartner && state.selectedPartner.id === action.payload.id) {
          state.selectedPartner = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePartnerAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Partner
      .addCase(deletePartnerAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePartnerAction.fulfilled, (state, action) => {
        state.loading = false;
        state.partners = state.partners.filter(p => p.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedPartner && state.selectedPartner.id === action.payload) {
          state.selectedPartner = null;
        }
        state.error = null;
      })
      .addCase(deletePartnerAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Activate Partner
      .addCase(activatePartnerAction.fulfilled, (state, action) => {
        const index = state.partners.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.partners[index] = action.payload;
        }
        if (state.selectedPartner && state.selectedPartner.id === action.payload.id) {
          state.selectedPartner = action.payload;
        }
      })
      // Deactivate Partner
      .addCase(deactivatePartnerAction.fulfilled, (state, action) => {
        const index = state.partners.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.partners[index] = action.payload;
        }
        if (state.selectedPartner && state.selectedPartner.id === action.payload.id) {
          state.selectedPartner = action.payload;
        }
      });
  },
});

export const {
  setLoading,
  setPartners,
  setSelectedPartner,
  setFilters,
  setError,
  addPartner,
  updatePartner,
  removePartner,
  reset,
} = partnersSlice.actions;

export default partnersSlice.reducer;